import api from '@pray/shared/utils/api';

export type SfSurfaceFamily = 'onboarding' | 'popup';

/**
 * The outcome a surface actually records.
 *
 * A lead surface writes a lead naming itself; a paywall writes no lead at all and its outcome is
 * the subscription. Reporting both on every surface counted one journey twice — the same user as a
 * lead on the lead offer and as a subscriber on it, when the subscription happened at the paywall
 * a minute later.
 */
export type SfOutcomeKind = 'leads' | 'subscriptions';

/**
 * The money a leader paid, and the two denominators it can be divided by.
 *
 * `charged` is what we invoiced. `counted` is the impressions that carried money — the denominator
 * the contract uses. `real` is every impression we actually served them. The two rates divide the
 * same numerator by those two denominators, and the distance between them is the point of the page.
 *
 * `quoted_cpm_cents` and `true_cpm_cents` are cents per thousand impressions, as the server computes
 * them. The panel renders per-impression, because that is the unit a leader's deal is written in.
 */
export type SfRateTotals = {
  charged_cents: number;
  counted_impressions: number;
  real_impressions: number;
  /**
   * Distinct people, counted once across the whole window.
   *
   * Its own pass server-side. Counting distinct per (day, surface, leader) and adding those up
   * counts anyone seen twice twice — measured at 229,597 against a true 107,179, which collapsed
   * impressions-per-person to 1.0 and made the figure read as though nobody saw us more than once.
   */
  reach: number;
  quoted_cpm_cents: number | null;
  true_cpm_cents: number | null;
  /** How far below the quoted rate the true rate sits, in percent. */
  rate_gap_pct: number | null;
  leads: number;
  /** Subscriptions attributed to a paywall surface, each counted exactly once. */
  subscriptions: number;
  /**
   * Spend over leads. There is no cost-per-subscription counterpart: the surfaces that produce
   * subscriptions are not charged for, and the surface that is charged for produces leads, so
   * dividing one by the other would price a surface by another surface's output. Its absence is
   * the pricing finding, not an omission.
   */
  cost_per_lead_cents: number | null;
};

export type SfSurfaceRow = {
  placement: string;
  family: SfSurfaceFamily;
  impressions: number;
  charged_cents: number;
  /** True when this surface's rows carried money. Read from the money, never from a flag. */
  charges: boolean;
  /** Which of the two counts below this surface records. The other is always 0 — render it blank. */
  outcome_kind: SfOutcomeKind;
  leads: number;
  subscriptions: number;
  /**
   * The surface's own outcome per thousand impressions, to one decimal.
   *
   * Raw counts are not comparable across these surfaces: popup_paywall began recording on
   * 2026-09-16 and email_lead on 2025-09-01, so they differ by an order of magnitude for reasons
   * that have nothing to do with performance. A rate is comparable on day one.
   */
  outcomes_per_1k: number | null;
  /**
   * Share of everything we served, against share of each outcome. Comparing the three is the
   * pricing signal: a surface taking 40% of the impressions and returning 5% of the subscriptions
   * is worth less per impression than one we charge for, not more.
   */
  impression_share_pct: number | null;
  lead_share_pct: number | null;
  subscription_share_pct: number | null;
};

export type SfLeaderRow = SfRateTotals & {
  artist_id: string | null;
  artist_name: string | null;
  surfaces: SfSurfaceRow[];
};

export type SfTrueRateDailyRow = {
  date: string;
  charged_cents: number;
  counted_impressions: number;
  real_impressions: number;
  leads: number;
  subscriptions: number;
  true_cpm_cents: number | null;
  /** One surface's day. A surface with no rows that day is absent, not zero. */
  by_placement: Record<string, SfDailySurface>;
};

/**
 * A surface on a single day.
 *
 * `outcomes` is the surface's own outcome and nothing else — leads for a lead surface,
 * subscriptions for a paywall. See SfOutcomeKind.
 *
 * ⚠️ `people` is a distinct count and is NOT ADDITIVE. It is counted per (day, surface) in its own
 * server pass, so a person who came on two days is one person over the window and two here. Read
 * it as a denominator for that day's frequency and nothing else; the window-wide figure is
 * SfRateTotals.reach. Adding distinct counts is what made reach read 229,597 against a true
 * 107,179.
 */
export type SfDailySurface = {
  impressions: number;
  outcomes: number;
  people: number;
};

/**
 * When each surface first recorded anything, ever.
 *
 * Without it a surface that did not exist yet reads exactly like one performing badly — the popup
 * paywall recorded its first impression on 2026-09-16, so a month-long window shows it flat at zero
 * for twenty-nine days and invites the wrong conclusion.
 */
export type SfSurfaceOrigin = { placement: string; recording_since: string | null };

export type SfTrueRate = {
  snapshot_date: string;
  range: { from: string; to: string };
  fleet: SfRateTotals;
  surfaces: SfSurfaceRow[];
  leaders: SfLeaderRow[];
  leader_total_count: number;
  daily: SfTrueRateDailyRow[];
  /** Subscriptions are counted this many days after the impression that preceded them. */
  subscription_attribution_days: number;
  surface_origins: SfSurfaceOrigin[];
  /** Leads are counted this many minutes after the impression that produced them. */
  lead_attribution_minutes: number;
  /**
   * What share of the leads we captured can actually be tied to an impression.
   *
   * Leads are attributed through the impression, the same way subscriptions are, so a lead we
   * cannot link is not counted anywhere else on the page. The gap is reported rather than hidden
   * inside a rate, because it is not evenly spread: measured over 14 days the onboarding lead offer
   * links 53.6% of its leads and the popup lead offer 7.3%, with 86.6% of popup-lead users having
   * no marketing impression of any kind in the preceding day. A low figure here is a broken link,
   * not a surface performing badly.
   */
  lead_coverage: SfLeadCoverage[];
};

/**
 * Captured against linked, per lead placement and source.
 *
 * ⚠️ `placement` is a LeadPlacements value, which is NOT the surface axis even though two of its
 * values share their spelling with MarketingPlacements. `popups` there means the account was at
 * least a day old when the lead was captured, and `superfunnel` means it was newer — both written
 * by the same popup flow. `superfunnel` also carries web and newsletter leads, which have no in-app
 * impression at all.
 *
 * A low linked share is not a verdict on a surface. Lead-surface impressions dedup once per device
 * per 24 hours, so for anyone past their first view of the day the impression that produced the
 * lead was never recorded.
 */
export type SfLeadCoverage = {
  placement: string;
  source: string | null;
  /** ios, android or web. The onboarding surfaces are instrumented on iOS only. */
  platform: string | null;
  captured: number;
  linked: number;
};

export type GetSfImpressionsParams = {
  /** Window, 'YYYY-MM-DD'. Omitted = the trailing 30 days. */
  from?: string | null;
  to?: string | null;
  /** Public artist UUID. Omitted = fleet. */
  leaderId?: string | null;
  limit?: number | null;
};

export async function getSfImpressions({
  from = null,
  to = null,
  leaderId = null,
  limit = null,
}: GetSfImpressionsParams = {}): Promise<SfTrueRate> {
  const params: Record<string, string | number | boolean> = {};

  if (from) params.from = from;
  if (to) params.to = to;
  if (leaderId) params.leader_id = leaderId;
  if (limit != null) params.limit = limit;

  const url = '/web/studio/system-check/superfunnel/impressions';
  const response = await api.executeGetRequest<{ data: SfTrueRate }>(url, params);

  return response.data.data;
}
